// src/oura/merchant/drawers/DidDrawer.tsx
//
// One of the latest actions: what we did, when, why it matters, and the
// evidence attached to it. Opened from a row of Latest actions on Work.
//
// `why` and evidence links are optional and absent on live today (BE handoff
// "Activity kind, why it matters, evidence links"). Without them the drawer
// says what was done and claims nothing more: evidence is listed only where it
// has a link, because on a merchant screen every row opens something.

import { useEffect, useRef, useState } from "react";
import { ExternalLinkIcon, Loader2Icon } from "lucide-react";
import { fetchActivityAttachment, isActivityAttachment, type ClientActivityEntry } from "@service/aeo/clientReview";
import { Drawer, DrawerBlock, DrawerHead, FOCUS_RING, TYPE, WorkKindIcon, returnFocusChain } from "@oura/ui";
import { cn } from "../../lib/cn";
import { dayDate } from "../../lib/oura";

/** A hairline above every evidence row but the first. */
const RULE = "border-t border-gray-100 first:border-t-0";
const LINK = cn("flex min-h-12 items-center gap-2 py-2 text-left font-medium text-blue-600 hover:underline", FOCUS_RING);

/** How long an opened file's blob URL lives: long enough for the new tab to load it. */
const BLOB_LIFETIME_MS = 60_000;

/**
 * An attachment, which the server serves only to a signed-in caller: fetched
 * with the app's credentials and opened from a blob URL in a new tab. A plain
 * link would reach the route with no Authorization header and be refused.
 */
function AttachmentRow({ label, path }: { label: string; path: string }) {
    const [state, setState] = useState<"idle" | "opening" | "failed">("idle");
    // Blob URLs still waiting to be revoked, freed early if the drawer closes.
    const pending = useRef(new Map<string, ReturnType<typeof setTimeout>>());
    useEffect(() => {
        const timers = pending.current;
        return () => timers.forEach((timer, url) => {
            clearTimeout(timer);
            URL.revokeObjectURL(url);
        });
    }, []);

    const open = async () => {
        setState("opening");
        try {
            const url = URL.createObjectURL(await fetchActivityAttachment(path));
            window.open(url, "_blank", "noopener,noreferrer");
            pending.current.set(url, setTimeout(() => {
                URL.revokeObjectURL(url);
                pending.current.delete(url);
            }, BLOB_LIFETIME_MS));
            setState("idle");
        } catch {
            setState("failed");
        }
    };

    return (
        <div className={RULE}>
            <div className="flex items-center gap-2">
                <button type="button" onClick={() => void open()} disabled={state === "opening"} aria-busy={state === "opening"} className={cn(LINK, "disabled:cursor-wait")}>
                    {label}
                </button>
                {state === "opening" && (
                    <span className={cn(TYPE.tc, "inline-flex items-center gap-1.5 text-gray-500")}>
                        <Loader2Icon aria-hidden size={14} className="animate-spin" /> Opening
                    </span>
                )}
            </div>
            {state === "failed" && (
                <p role="alert" className={cn(TYPE.tc, "m-0 pb-2 text-rose-700")}>
                    {label} did not open. Try again in a moment.
                </p>
            )}
        </div>
    );
}

export function DidDrawer({ entry, onClose }: { entry: ClientActivityEntry | null; onClose: () => void }) {
    if (!entry) return null;
    const evidence = (entry.evidence ?? []).filter((e): e is { label: string; url: string } => Boolean(e.url));
    return (
        <Drawer open onClose={onClose} returnFocus={returnFocusChain(`[data-action-id="${CSS.escape(entry.id)}"]`)}>
            <DrawerHead
                lead={<WorkKindIcon kind={entry.kind} size={14} />}
                title={entry.body}
                status={dayDate(entry.at) ? { label: dayDate(entry.at) ?? "" } : null}
            />
            {entry.meta && <DrawerBlock>{entry.meta}</DrawerBlock>}
            {entry.why && <DrawerBlock label="Why it matters">{entry.why}</DrawerBlock>}
            {evidence.length > 0 && (
                <DrawerBlock label="Evidence">
                    {evidence.map((e, i) =>
                        isActivityAttachment(e.url) ? (
                            <AttachmentRow key={`${e.url}-${i}`} label={e.label} path={e.url} />
                        ) : (
                            // The merchant's own live page: a public address.
                            <a key={`${e.url}-${i}`} href={e.url} target="_blank" rel="noopener noreferrer" className={cn(RULE, LINK)}>
                                {e.label}
                                <ExternalLinkIcon aria-hidden size={14} className="shrink-0" />
                            </a>
                        ),
                    )}
                </DrawerBlock>
            )}
        </Drawer>
    );
}
